import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  CheckCircle2, AlertTriangle, XCircle, ArrowUpCircle, Package,
  Clock, Search, ChevronDown, ChevronRight, Zap, Globe
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (val) => {
  if (!val) return "₹0";
  if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)}Cr`;
  if (val >= 100000) return `₹${(val / 100000).toFixed(2)}L`;
  return `₹${Math.round(val).toLocaleString("en-IN")}`;
};

const UploadSummaryDialog = ({ open, onClose, result, token, onRefresh }) => {
  const [expandedError, setExpandedError] = useState(null);
  const [suggestions, setSuggestions] = useState({});
  const [loadingSuggest, setLoadingSuggest] = useState(null);
  const [fixing, setFixing] = useState(null);
  const [revertingLocale, setRevertingLocale] = useState(false);
  const [localeReverted, setLocaleReverted] = useState(false);

  if (!result) return null;

  const summary = result.summary || {};
  const stats = result.upload_stats || {};
  const errors = stats.error_breakdown || [];
  const totalParcels = summary.total_orders || 0;
  const errCount = summary.orders_with_errors || 0;
  const zeroCost = stats.zero_cost || 0;
  const highValue = stats.high_value || 0;
  const savedCount = result.saved_count || 0;
  const skipped = result.skipped_duplicates || 0;
  const localeCorrection = result.stitch_stats?.locale_correction;
  const batchId = result.batch_id;

  const fetchSuggestions = async (productName) => {
    if (suggestions[productName]) { setExpandedError(productName); return; }
    setLoadingSuggest(productName);
    try {
      const res = await fetch(`${BACKEND_URL}/api/shipment-costing/suggest-product?name=${encodeURIComponent(productName)}&limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSuggestions(prev => ({ ...prev, [productName]: data.suggestions || [] }));
        setExpandedError(productName);
      }
    } catch (e) { console.error(e); }
    finally { setLoadingSuggest(null); }
  };

  const handleBulkFix = async (wrongName, correctName) => {
    setFixing(wrongName);
    try {
      const res = await fetch(`${BACKEND_URL}/api/shipment-costing/bulk-fix-product`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ wrong_name: wrongName, correct_name: correctName })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Fixed ${data.updated} orders: "${wrongName}" → "${correctName}"`);
        onRefresh?.();
      } else {
        toast.error(data.detail || "Fix failed");
      }
    } catch (e) { toast.error(e.message); }
    finally { setFixing(null); }
  };

  const handleRevertLocale = async () => {
    if (!batchId) {
      toast.error("Batch id missing — cannot revert");
      return;
    }
    if (!window.confirm(
      "Revert locale correction?\n\n" +
      "This will swap day↔month back on every parcel in this upload — only do " +
      "this if you're sure your file was actually in MM/DD format.\n\n" +
      "Continue?"
    )) return;
    setRevertingLocale(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/shipment-costing/upload-audits/${batchId}/revert-locale-correction`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (res.ok) {
        toast.success(`Reverted ${data.rows_reverted} parcel(s).`);
        setLocaleReverted(true);
        onRefresh?.();
      } else {
        toast.error(data.detail || "Revert failed");
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRevertingLocale(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col" data-testid="upload-summary-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-600" />
            Upload Complete
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 space-y-3 -mx-1 px-1">
          {/* Key Stats */}
          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-center gap-2 p-2.5 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
              <div>
                <p className="text-lg font-bold text-green-700">{totalParcels.toLocaleString()}</p>
                <p className="text-[10px] text-green-600">Parcels processed</p>
              </div>
            </div>
            <div className="flex items-center gap-2 p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
              <Zap className="w-5 h-5 text-blue-600 shrink-0" />
              <div>
                <p className="text-lg font-bold text-blue-700">{savedCount.toLocaleString()}</p>
                <p className="text-[10px] text-blue-600">Saved to DB{skipped > 0 ? ` (${skipped} dup skipped)` : ""}</p>
              </div>
            </div>
          </div>

          {/* Alerts Row */}
          <div className="space-y-2">
            {errCount > 0 && (
              <div className="flex items-center justify-between p-2.5 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-500" />
                  <span className="text-sm font-medium text-red-700">{errCount} with errors</span>
                </div>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600" onClick={() => { onClose(); window.location.href = "/errors"; }}>
                  Resolve Now
                </Button>
              </div>
            )}
            {zeroCost > 0 && (
              <div className="flex items-center justify-between p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  <span className="text-sm font-medium text-amber-700">{zeroCost} zero cost parcels</span>
                </div>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-amber-600" onClick={() => { onClose(); window.location.href = "/costing"; }}>
                  Review
                </Button>
              </div>
            )}
            {highValue > 0 && (
              <div className="flex items-center gap-2 p-2.5 bg-orange-50 border border-orange-200 rounded-lg">
                <ArrowUpCircle className="w-4 h-4 text-orange-500" />
                <span className="text-sm font-medium text-orange-700">{highValue} high value (&gt;₹50K) — verify pricing</span>
              </div>
            )}
            {errCount === 0 && zeroCost === 0 && (
              <div className="flex items-center gap-2 p-2.5 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">No errors found</span>
              </div>
            )}
          </div>

          {/* Cost Summary */}
          <div className="flex gap-3 text-xs text-slate-600 bg-slate-50 p-2.5 rounded-lg">
            <span>Med: <strong>{fmt(summary.total_medicine_cost)}</strong></span>
            <span>Ship: <strong>{fmt(summary.total_shipping_cost)}</strong></span>
            <span>Total: <strong>{fmt(summary.total_cost)}</strong></span>
            {summary.processing_time_seconds && <span className="ml-auto text-slate-400"><Clock className="w-3 h-3 inline mr-0.5" />{summary.processing_time_seconds}s</span>}
          </div>

          {/* Locale auto-correction fingerprint + override */}
          {localeCorrection?.applied && (
            <div
              className={`border rounded-lg p-3 ${
                localeReverted || localeCorrection.reverted
                  ? "border-slate-200 bg-slate-50"
                  : "border-amber-200 bg-amber-50"
              }`}
              data-testid="locale-fingerprint-panel"
            >
              <div className="flex items-start gap-2">
                <Globe className={`w-4 h-4 mt-0.5 shrink-0 ${
                  localeReverted || localeCorrection.reverted ? "text-slate-500" : "text-amber-700"
                }`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-semibold ${
                    localeReverted || localeCorrection.reverted ? "text-slate-700" : "text-amber-900"
                  }`}>
                    {localeReverted || localeCorrection.reverted
                      ? "Locale correction reverted"
                      : `Date locale auto-corrected — ${(localeCorrection.rows_swapped || 0).toLocaleString()} row(s) rescued`}
                  </p>
                  <p className={`text-[11px] mt-0.5 ${
                    localeReverted || localeCorrection.reverted ? "text-slate-600" : "text-amber-800"
                  }`}>
                    {localeReverted || localeCorrection.reverted ? (
                      <>Day↔month swapped back. Dates now follow the file's original MM/DD interpretation.</>
                    ) : (
                      <>
                        Excel saved this workbook in <strong>MM/DD</strong> format. EximFlow detected
                        DD/MM intent and filed dates in their correct month — so <strong>6/3/2026</strong> stays in March, not June.
                      </>
                    )}
                  </p>
                  <p className="text-[10px] text-slate-500 italic mt-0.5 truncate" title={localeCorrection.reason}>
                    Signal: {localeCorrection.reason || "n/a"}
                  </p>
                </div>
                {!localeReverted && !localeCorrection.reverted && batchId && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-[11px] border-amber-300 text-amber-800 hover:bg-amber-100"
                    onClick={handleRevertLocale}
                    disabled={revertingLocale}
                    data-testid="revert-locale-btn"
                  >
                    {revertingLocale ? "Reverting…" : "Trust my dates (revert)"}
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Error Breakdown with Quick Fix */}
          {errors.length > 0 && (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-slate-100 text-xs font-medium text-slate-600">Error Breakdown — Click to suggest fix</div>
              {errors.map((e) => {
                const isProductError = e.type.startsWith("Product not found");
                const productName = isProductError ? e.type.replace("Product not found", "").replace(":", "").trim() : "";
                const isExpanded = expandedError === productName;
                const sugs = suggestions[productName] || [];

                return (
                  <div key={e.type} className="border-t border-slate-100">
                    <div
                      className={`flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-slate-50 ${isExpanded ? 'bg-blue-50' : ''}`}
                      onClick={() => isProductError ? fetchSuggestions(productName) : null}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {isProductError ? (isExpanded ? <ChevronDown className="w-3 h-3 text-slate-400 shrink-0" /> : <ChevronRight className="w-3 h-3 text-slate-400 shrink-0" />) : <XCircle className="w-3 h-3 text-red-400 shrink-0" />}
                        <span className="text-xs truncate">{e.type}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant="destructive" className="text-[10px]">{e.count}</Badge>
                        {isProductError && (
                          <span className="text-[10px] text-blue-500">
                            {loadingSuggest === productName ? "Loading..." : "Suggest"}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Suggestions panel */}
                    {isExpanded && isProductError && sugs.length > 0 && (
                      <div className="px-3 pb-2 bg-blue-50 space-y-1">
                        <p className="text-[10px] text-blue-600 font-medium">Similar products found:</p>
                        {sugs.map((s, i) => (
                          <div key={i} className="flex items-center justify-between bg-white rounded px-2 py-1.5 border border-blue-100">
                            <div className="text-xs">
                              <span className="font-medium">{s.product_name}</span>
                              {s.price && <span className="text-slate-500 ml-2">₹{s.price}</span>}
                            </div>
                            <Button
                              size="sm" variant="ghost"
                              className="h-6 text-[10px] text-emerald-600 hover:text-emerald-800 px-2"
                              disabled={fixing === productName}
                              onClick={(ev) => { ev.stopPropagation(); handleBulkFix(productName, s.product_name); }}
                            >
                              {fixing === productName ? "Fixing..." : `Apply to ${e.count}`}
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {isExpanded && isProductError && sugs.length === 0 && loadingSuggest !== productName && (
                      <div className="px-3 pb-2 bg-slate-50 text-[10px] text-slate-500">No similar products found</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          <Button size="sm" onClick={() => { onClose(); window.location.href = "/costing"; }}>
            View in Shipment Costing
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UploadSummaryDialog;
